import { Location } from 'history';
import { isAndroid, isIOS } from 'react-device-detect';
import { NavigateFunction } from 'react-router-dom';

import { UserDto } from 'app/api/apiClient/generated/types';
import { keys } from 'app/config/keys';
import { adminRoutes, externalRoutes, factorRoutes, payeeRoutes, payorRoutes, routes } from 'app/constants/routes';

import { cleanObject } from './object';
import { parseSearch, stringifyQueryParams } from './string';
import { isFactor, isPayor } from './user';

// Local Variables
const payorUserRoute = `/${routes.payor}/${payorRoutes.users}`;
const payeeUserRoute = `/${routes.payee}/${payeeRoutes.users}`;
const factorUserRoute = `/${routes.factor}/${factorRoutes.users}`;

export const getAuditUrl = (id: string) =>
  `${keys.CORE_URL}/${externalRoutes.audit}/${id}`;

export const handleRedirectToAudit = (id: string) => () => {
  const url = `${keys.CORE_URL}/${externalRoutes.audit}/${id}`;

  window.open(url,
    '_blank');
};

export const handleRedirectToAdmin = () => {
  window.location.href = `${keys.CORE_URL}/${externalRoutes.admin}`;
};

export const handleRedirectToNoaOpsQueue = () => {
  window.location.href = `/${routes.admin}/${adminRoutes.noaActionConflicts}`;
};

export function attemptToOpenApp(path: string) {
  const notIOSOrAndroid = !isAndroid && !isIOS;

  if (notIOSOrAndroid) {
    return null;
  }

  if (!keys.APP_NAME) {
    return;
  }

  window.location.href = `${keys.APP_NAME}${path}`;
}

export function attemptToGoToAppStore() {
  const notIOSOrAndroid = !isAndroid && !isIOS;

  if (notIOSOrAndroid) {
    return null;
  }

  const {
    APP_ANDROID_APP_STORE_LINK: androidAppUrl,
    APP_IOS_APP_STORE_LINK: iosAppUrl,
  } = keys;
  const appStoreUrl = isIOS ? iosAppUrl : androidAppUrl;

  if (!appStoreUrl) {
    return;
  }

  window.location.href = appStoreUrl;
}

export function goToHomePage(search?: string) {
  window.location.href = `${window.location.origin}${search ?? ''}`;
}

export function handleNavigateParams({
  navigate,
  params,
  pathname,
  search,
}: {
  navigate: NavigateFunction;
  params: Record<string, unknown>;
  pathname: string;
  search: string;
}) {
  const currentParams = parseSearch(search);

  const queryParams: Record<string, string | string[]> = cleanObject({
    ...currentParams,
    ...params,
  });
  const queryString = stringifyQueryParams(queryParams);

  // prevent potential infinite loop
  if (search === `?${queryString}`) {
    return;
  }

  navigate({
    pathname,
    search: `?${queryString}`,
  },
  {
    replace: true,
  });
}
export const getCreateEditUserRoute = (user: null | UserDto) => {
  if (isFactor(user)) {
    return factorUserRoute;
  }
  if (isPayor(user)) {
    return payorUserRoute;
  }

  return payeeUserRoute;
};

export const buildJumpLink = (companyGuid: string,
  redirect: string): string => {
  // Redirect parameter isn't included in stringifyQueryParams call to avoid encoding slashes in the path
  return `${keys.CORE_URL}/InternalReport/Jump?${stringifyQueryParams({ companyGuid })}&redirect=${redirect}`;
};

export const buildLoginLink = (returnUrl?: Location | string) => {
  if (!returnUrl) {
    return '/';
  }
  const newUrl = typeof returnUrl === 'string' ? returnUrl : `${returnUrl.pathname}${returnUrl.search}`;

  return `/?return_url=${encodeURIComponent(newUrl)}`;
};
